import { useEffect, useRef, useState } from 'react'
import { useOptionalDeviceCache } from '../device-cache/context'

/**
 * The launch screen — the app's own mark, full-frame, for as long as the shell has nothing
 * truthful to show.
 *
 * **What it replaces, and why that was not a splash.** The whole of the previous boot UI was the
 * word *"טוען…"* centred in an empty frame, which lifted the moment the session resolved from
 * IndexedDB — before the gate list existed. What a resident actually watched after that was the app
 * bar's refresh glyph spinning through the boot `GET /gates`, at 900 ms a turn: "the refresh glyph
 * rotates a few times on startup" was that spinner, reporting a fetch nobody had asked for. D178
 * removes the glyph and gives the same seconds an identity instead.
 */

/**
 * The ceiling. Nothing holds the launch screen longer than this, whatever the network is doing.
 *
 * **Exported for one assertion, not for a second reader.** `DeviceCacheProvider`'s boot retry is
 * allowed to clear `lastRefreshFailed` — which re-satisfies clause 2 below — only because its own
 * schedule outlasts this ceiling, so nothing can be in a failed state early enough for the clear
 * to bring the launch screen back. That inequality is asserted rather than assumed; nothing
 * *reads* this value outside this module.
 */
export const MAX_HOLD_MS = 2500

/** Matches `.boot-splash.leaving`'s transition in `global.css`. */
const FADE_MS = 180

/**
 * Whether the launch screen should still be up.
 *
 * **Called by `App` before its own boot gate**, because it is a hook and the gate is an early
 * return. It reads the device cache optionally for the same reason `AppBar` does: this is chrome,
 * and a render with no provider mounted must degrade rather than throw.
 *
 * Three clauses, and the middle one is the whole design:
 *
 * 1. **Auth must have resolved.** Below that there is no route to render into.
 * 2. **A signed-in launch waits for the gate list to settle — but "settle" is not "succeed".**
 *    `origin` flips to `'restored'` the instant a cached snapshot is read, so a returning resident
 *    sees the mark for a few hundred milliseconds and then their own gates, with the network fetch
 *    still running silently behind them. A first-ever launch has no snapshot, so it waits for the
 *    fetch itself — which is the case the complaint was actually about. A failed fetch
 *    (`lastRefreshFailed`) and an offline launch (`!online`, where `DeviceCacheProvider` never calls
 *    `refresh()` at all) both settle immediately: nothing is coming, and holding the mark would be a
 *    promise of an answer.
 * 3. **{@link MAX_HOLD_MS} outranks all of it.** A launch screen that can outlive its own reasons is
 *    a hang with a logo on it. Every branch above is a prediction about what will resolve; this is
 *    the one clause that does not depend on being right.
 */
export function useBootSplashVisible(authResolved: boolean, signedIn: boolean): boolean {
  const cache = useOptionalDeviceCache()
  const [ceilingReached, setCeilingReached] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => setCeilingReached(true), MAX_HOLD_MS)
    return () => clearTimeout(timer)
  }, [])

  if (ceilingReached) return false
  if (!authResolved) return true
  if (!signedIn || cache === undefined) return false
  return !(cache.origin !== undefined || cache.lastRefreshFailed === true || !cache.online)
}

/**
 * **No entry animation, deliberately.** The layer is opaque `--g0` from its first paint, which is
 * also the document's background and the `theme-color` — so it composes with, rather than covers,
 * whatever the OS drew before the bundle ran. It also means the remount that happens when `App`
 * crosses from its auth-loading branch into the routed one is invisible: one opaque cream layer
 * replaces an identical opaque cream layer.
 *
 * The exit is the only animated part, and `visibleRef` is what keeps it honest — mounting already
 * hidden renders nothing rather than fading out something that was never seen.
 */
export function BootSplash({ visible }: { readonly visible: boolean }): JSX.Element | null {
  const wasVisible = useRef(visible)
  const [present, setPresent] = useState(visible)

  useEffect(() => {
    if (visible) {
      wasVisible.current = true
      setPresent(true)
      return
    }
    if (!wasVisible.current) {
      setPresent(false)
      return
    }
    const timer = setTimeout(() => setPresent(false), FADE_MS)
    return () => clearTimeout(timer)
  }, [visible])

  if (!present) return null
  return (
    <div className={`boot-splash${visible ? '' : ' leaving'}`} aria-hidden="true">
      {/*
        The mark is painted by `.boot-splash-mark`'s `background-image` rather than carried here as
        an `<img src>`, and the reason is D194's, arriving one screen late. The asset is the same
        one `AboutScreen` renders, generated from `brand/selogate-mark.svg` by `npm run icons` and
        precached by `globPatterns` — so a cold *offline* launch, which is exactly when a blank
        frame is least reassuring, still has it. But `favicon.svg` serves both palettes from **one**
        file and picks between them with its own internal `prefers-color-scheme` block, which is
        the *platform's* answer and unreachable from this stylesheet: since D188 the app's scheme is
        a preference on `[data-scheme]`, so the launch screen greeted a resident on the graphite
        default with a cream mark whenever the phone itself was in light. The two palette-locked
        siblings carry no media query, and only a background-image can be switched by a
        `[data-scheme]` rule. An empty element rather than an `<img alt="">` because there is
        nothing left for a `src` to name; the layer is already `aria-hidden`.

        Sized in CSS against the frame's short side; the mark carries its own plate, a shade off
        `--g0` in both schemes, so it is presented as a card rather than bled to the edges.
      */}
      <div className="boot-splash-mark" />
    </div>
  )
}
